import GenericBffClient from 'Utils/httpClients/GenericBffClient';
import { DOMAIN, LoggerFactory } from 'Services/Logger';

export const CONTACT_TYPE = {
  MOBILE_PHONE: 'MOBILE_PHONE',
};

const logger = LoggerFactory.getLogger(DOMAIN.KUESKI);

export class ConsumerEnablementBff extends GenericBffClient {
  constructor() {
    super('CONSUMER_ENABLEMENT_BFF_API', '/api/v1/', { logger });
  }

  getContact() {
    return this.get('profile/contact');
  }

  requestNIP(mobileNumber = '') {
    const payload = {
      contact_type: CONTACT_TYPE.MOBILE_PHONE,
      value: mobileNumber.toString(),
    };

    return this.post('profile/contact/otp', payload);
  }

  /**
   * @param {{ mobileNumber: string, nip: string, channel: 'web-mobile' | 'web-desktop' }} options
   * @returns { Promise<any> }
   */
  verifyPhoneAndNIP({ mobileNumber = '', nip = '', channel }) {
    const payload = {
      channel,
      contact_type: CONTACT_TYPE.MOBILE_PHONE,
      otp: nip.toString(),
      value: mobileNumber.toString(),
    };

    return this.post('profile/contact/verify', payload);
  }

  /**
   * @param {{ mobileNumber: string, channel: 'web-mobile' | 'web-desktop' }} options
   * @returns { Promise<any> }
   */
  validatePhone({ mobileNumber, channel }) {
    const payload = {
      channel,
      contact_type: CONTACT_TYPE.MOBILE_PHONE,
      value: mobileNumber ?? '',
    };

    return this.post('profile/contact/validate', payload);
  }

  getAddress() {
    return this.get('profile/address');
  }

  getPersonalInfo() {
    return this.get('profile/personal_info');
  }

  getUserFeatureFlags() {
    return this.get('features');
  }

  getAdditionalInfo() {
    return this.get('profile/financial_info');
  }

  sendNIPByEmail({ method = 'email', phone }) {
    const payload = {
      send_method_name: method,
      value: phone?.replace(/\s/g, ''),
    };

    return this.post('profile/contact/otp/send', payload);
  }

  validateNIP({ nip, token }) {
    return this.post(
      'profile/contact/otp/validate',
      { otp: nip, token_id: token },
    );
  }

  validateNIPPhone({
    emailToken,
    nip,
    phone,
    token,
  }) {
    const payload = {
      email_token_id: emailToken,
      otp: nip,
      token_id: token,
      value: phone?.replace(/\s/g, ''),
    };

    return this.post('profile/contact/otp/phone/verify', payload);
  }

  getUpdatePhoneRequest() {
    return this.get('profile/contact/validate_request');
  }

  sendFillingReminderEmail({ merchantName, returnUrl }) {
    const payload = {
      merchant_name: merchantName,
      return_url: returnUrl,
    };

    return this.post('events/send', payload);
  }

  getUserEvaluation() {
    return this.get('users/evaluation');
  }

  /**
   * @returns { Promise<import('./ConsumerEnablementBff.types').UserVerification> }
   */
  getVerification() {
    return this.get('users/verification');
  }

  /**
   * @param {{
   *  activityId?: string,
   *  method?: 'email' | 'sms' | 'whatsapp' | 'phone',
   *  phone?: string,
   * }} options
   * @param {'email' | 'sms' | 'whatsapp'} [options.method='email'] - 'email' by default.
   * @returns { Promise<any> }
   */
  sendOTP({ activityId, method = 'email', phone }) {
    const payload = {
      activity_id: activityId,
      send_method_name: method,
      value: phone?.replace(/\D/g, ''),
    };

    return this.post('profile/contact/otp/send', payload);
  }

  validateOTP({ otp, token, activityId }) {
    return this.post(
      'profile/contact/otp/validate',
      { otp, token_id: token, ...(activityId ? { activity_id: activityId } : {}) },
    );
  }

  getActionRequired({ activityId }) {
    return this.get(
      `profile/contact/activity_status/${activityId}`,
    );
  }

  /**
   * @param {{
   *   activityId?: string,
   *   authToken: string,
   *   method?: 'email' | 'sms' | 'whatsapp' | 'phone',
   * }} options
   * @param {'email' | 'sms' | 'whatsapp'} [options.method='email'] - 'email' by default.
   * @returns { Promise<SendOTPResponse> }
   * TODO: replace the endpoint
   */
  sendOTPWithoutSession({ method = 'email', activityId, authToken }) {
    const payload = {
      activity_id: activityId,
      send_method_name: method,
    };

    return this.post(
      'authentication/send_otp',
      payload,
      { specialToken: authToken },
    );
  }

  /**
   * @param {{ otp: string, token: string, activityId?: string, authToken: string }} options
   * @returns { Promise<ValidateOTPResponse> }
   * TODO: replace the endpoint
   */
  validateOTPWithoutSession({
    otp, token, activityId, authToken,
  }) {
    return this.post(
      'authentication/validate_otp',
      { otp, token_id: token, ...(activityId ? { activity_id: activityId } : {}) },
      { specialToken: authToken },
    );
  }

  /**
   * @param {{ activityId?: string | null, authToken: string }} options
   * @returns { Promise<ActionRequiredResponse> }
   * TODO: replace the endpoint
   */
  getActionRequiredWithoutSession({ activityId, authToken }) {
    return this.get(
      `authentication/activity_status/${activityId}`,
      { specialToken: authToken },
    );
  }
}
